import { Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import type { Graph3DNodeRendererProps } from '../../core/renderer.types';
import { NodeTextHitPlane } from './NodeTextHitPlane';

const HTML_CONTENT_Z_INDEX_RANGE: [number, number] = [160, 0];

export function CardNode3D({
  node,
  runtime,
  theme,
  hovered,
  selected,
  focused,
  onHover,
  onSelect,
  onFocus,
}: Graph3DNodeRendererProps) {
  const status = runtime?.status ?? 'idle';
  const active =
    runtime?.active || runtime?.highlighted || hovered || selected || focused;
  const color = runtime?.color ?? theme.runtime[status] ?? theme.node.border;
  const opacity = runtime?.hidden ? 0.18 : 1;

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(node.id);
    onFocus(node.id);
  };

  return (
    <group
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      onClick={handleClick}
      onPointerEnter={event => {
        event.stopPropagation();
        onHover(node.id);
      }}
      onPointerLeave={event => {
        event.stopPropagation();
        onHover(null);
      }}
    >
      <mesh>
        <boxGeometry
          args={[
            node.resolvedSize.width,
            node.resolvedSize.height,
            node.resolvedSize.depth,
          ]}
        />
        <meshStandardMaterial
          color={theme.node.fill}
          emissive={color}
          emissiveIntensity={active ? 0.35 : 0.1}
          transparent
          opacity={opacity}
        />
      </mesh>
      <mesh position={[0, 0, node.resolvedSize.depth / 2 + 0.4]}>
        <boxGeometry
          args={[node.resolvedSize.width + 3, node.resolvedSize.height + 3, 1]}
        />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={active ? 0.45 : 0.2}
        />
      </mesh>
      <NodeTextHitPlane
        width={node.resolvedSize.width}
        height={node.resolvedSize.height}
        z={node.resolvedSize.depth / 2 + 2}
      />
      <Html
        center
        transform
        distanceFactor={400}
        zIndexRange={HTML_CONTENT_Z_INDEX_RANGE}
        position={[0, 0, node.resolvedSize.depth / 2 + 2]}
      >
        <div
          style={{
            minWidth: node.resolvedSize.width,
            maxWidth: node.resolvedSize.width,
            color: theme.node.text,
            fontSize: 12,
            lineHeight: '16px',
            textAlign: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          <div style={{ fontWeight: 700, whiteSpace: 'normal' }}>
            {node.label}
          </div>
          <div
            style={{ marginTop: 4, color: theme.node.mutedText, fontSize: 10 }}
          >
            {node.type}
          </div>
        </div>
      </Html>
    </group>
  );
}
